import clsx from "clsx";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useSearch } from "wouter";
import { SyncBadge } from "../../web/components/SyncBadge";
import { Notice, PageHeader, Panel } from "../../web/components/ui";
import { useMoney } from "../../web/lib/money";
import { rowOpen } from "../../web/lib/rowOpen";
import { bundleTotals } from "./bundleTotals";
import { ListToolbar, matchesSearch, NeedsAttention } from "./ListToolbar";
import type { PriceListData } from "./meta";
import { can } from "./data";
import { bundleNumberProblem, bundleProblems } from "./attention";
import { itemNumberIssues } from "./setup";

type Filter = "active" | "attention" | "changed" | "appOnly" | "archived" | "all";
/** The Show list (ListToolbar), as on the other lists. */
const SHOW_LABELS: Record<Filter, string> = { active: "Active bundles", attention: "Anything needing attention", changed: "Changed in ServiceM8", appOnly: "Only in this app", archived: "Archived", all: "All" };
const SHOW_GROUPS: { label: string | null; filters: Filter[] }[] = [
    { label: null, filters: ["active", "archived", "all"] },
    { label: "Needs attention", filters: ["attention", "changed"] },
    { label: "Kinds of bundle", filters: ["appOnly"] },
];
const FILTERS = Object.keys(SHOW_LABELS) as Filter[];

/** Whether a bundle belongs in the list for this filter (search aside). */
function inFilter(filter: Filter, b: { uuid: string; active: boolean; synced: boolean }, changed: ReadonlySet<string>, problems: readonly string[]) {
    if (filter === "active") return b.active;
    if (filter === "attention") return problems.length > 0;
    if (filter === "changed") return changed.has(b.uuid);
    if (filter === "archived") return !b.active;
    if (filter === "appOnly") return !b.synced;
    return true;
}

/** The bundles list. Clicking one opens it. */
export function BundlesList(props: Readonly<{ data: PriceListData; openUuid?: string; onOpen: (uuid: string) => void; onNew: () => void }>) {
    const { data } = props;
    const fmt = useMoney();
    const seesCosts = can(data, "costs.view");
    // A link can open it already searched (e.g. "See all" from Home's search): ?q=...
    const [query, setQuery] = useState(() => new URLSearchParams(globalThis.location.search).get("q") ?? "");
    // Links (e.g. from Home) can open it on a filter: ?filter=changed.
    const linked = new URLSearchParams(useSearch()).get("filter");
    const [filter, setFilter] = useState<Filter>(FILTERS.includes(linked as Filter) ? (linked as Filter) : "active");
    // Changed in ServiceM8, not seen yet (history.ts).
    const changed = new Set(data.changedInServiceM8.materialbundle);
    // What needs attention on each (attention.ts): its item number, archived items in it, changes from ServiceM8.
    const numbers = itemNumberIssues([...data.materials, ...data.bundles]);
    const items = new Map(data.materials.map((m) => [m.uuid, { name: m.name, active: m.active }]));
    const problems = new Map(data.bundles.map((b) => [b.uuid, bundleProblems(b, numbers, items, changed.has(b.uuid))]));
    const problemsOf = (b: (typeof data.bundles)[number]) => problems.get(b.uuid) ?? [];
    const counts = Object.fromEntries(FILTERS.map((f) => [f, data.bundles.filter((b) => inFilter(f, b, changed, problemsOf(b))).length])) as Record<Filter, number>;
    const rows = data.bundles.filter((b) => inFilter(filter, b, changed, problemsOf(b)) && matchesSearch(`${b.name} ${b.item_number}`, query));

    return (
        <div className="page is-wide">
            <PageHeader
                title="Bundles"
                subtitle="Several items added to a job together, each at its own price."
                actions={
                    can(data, "bundles") ? (
                        <button type="button" className="btn-primary" onClick={props.onNew}>
                            <Plus className="size-4" /> New bundle
                        </button>
                    ) : null
                }
            />
            {data.bundleSyncUnavailable ? (
                <div className="mb-4">
                    <Notice>{data.bundleSyncUnavailable}</Notice>
                </div>
            ) : null}
            <Panel flush>
                <ListToolbar
                    searchLabel="Search bundles"
                    placeholder="Search by name or item number"
                    query={query}
                    onQuery={setQuery}
                    filter={filter}
                    onFilter={setFilter}
                    groups={SHOW_GROUPS}
                    labels={SHOW_LABELS}
                    counts={counts}
                />
                <table className="list is-fixed min-w-[720px]">
                    <colgroup>
                        <col className="w-[46%]" />
                        <col className="w-[14%]" />
                        <col className="w-[20%]" />
                        {seesCosts ? <col className="w-[20%]" /> : null}
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Bundle</th>
                            <th className="text-right">Items</th>
                            <th className="text-right">Price</th>
                            {/* What the business pays: only with See costs (the server leaves item costs out otherwise). */}
                            {seesCosts ? <th className="text-right">Cost</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((b) => {
                            const totals = bundleTotals(data, b.material_list);
                            return (
                                // The name button covers the whole row (after:inset-0): click selects it, double-click opens the bundle.
                                <tr key={b.uuid} className={clsx("relative", problemsOf(b).length > 0 && (bundleNumberProblem(b, numbers) ? "is-danger" : "is-warning"), props.openUuid === b.uuid ? "bg-brand-500/10" : "hover:bg-surface-2 focus-within:bg-brand-500/5")}>
                                    <td className="h-14">
                                        <button type="button" className="block w-full min-w-0 cursor-default text-left outline-none after:absolute after:inset-0" {...rowOpen(() => props.onOpen(b.uuid))}>
                                            <span className="flex min-w-0 items-center gap-1.5">
                                                <SyncBadge synced={b.synced} />
                                                <span className={clsx("truncate font-semibold", !b.active && "text-muted line-through")}>{b.name || "Untitled bundle"}</span>
                                                <NeedsAttention problems={problemsOf(b)} danger={bundleNumberProblem(b, numbers)} />
                                            </span>
                                            <span className="block h-4 truncate text-xs text-muted">{b.item_number}</span>
                                        </button>
                                    </td>
                                    <td className="text-right">{b.material_list.length.toLocaleString()}</td>
                                    <td className="text-right">
                                        <div className="truncate">{fmt.money(totals.priceNet)}</div>
                                        <div className="h-4 truncate text-xs text-muted">before tax</div>
                                    </td>
                                    {seesCosts ? <td className="text-right">{fmt.money(totals.costNet)}</td> : null}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 ? <p className="p-6 text-center text-sm text-muted">{data.bundles.length ? "No bundles match." : "No bundles yet."}</p> : null}
            </Panel>
        </div>
    );
}
